/**
 * PerformancePage — tabbed landing page for the Performance domain.
 *
 * Tabs:
 *   - My Reviews — subject-of + reviewer-of lists, toggled.
 *   - My Goals   — owner=me list with progress bars + "New Goal".
 *   - 1:1s       — manager=me ∪ report=me, merged.
 *   - Cycles     — HR_ADMIN+ only; list + "New Cycle".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Target, CalendarClock, FileText } from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";

import {
  listReviews,
  listGoals,
  createGoal,
  listOneOnOnes,
  listReviewCycles,
  createReviewCycle,
  type Review,
  type Goal,
  type GoalCategory,
  type OneOnOne,
  type ReviewCycle,
  type ReviewCycleStatus,
  type ReviewStatus,
  type GoalStatus,
} from "@/api/performance";

import { PageHeader } from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { Tabs } from "@/components/ui/Tabs";
import { Skeleton } from "@/components/ui/Skeleton";

const REVIEW_STATUS_VARIANT: Record<
  ReviewStatus,
  "default" | "info" | "warning" | "success"
> = {
  NOT_STARTED: "default",
  IN_PROGRESS: "warning",
  SUBMITTED: "info",
  ACKNOWLEDGED: "success",
};

const GOAL_STATUS_VARIANT: Record<
  GoalStatus,
  "default" | "info" | "warning" | "success" | "danger"
> = {
  DRAFT: "default",
  ACTIVE: "info",
  ON_TRACK: "success",
  AT_RISK: "warning",
  COMPLETED: "success",
  CANCELLED: "danger",
};

const CYCLE_STATUS_VARIANT: Record<
  ReviewCycleStatus,
  "default" | "info" | "warning" | "success"
> = {
  DRAFT: "default",
  ACTIVE: "success",
  CALIBRATION: "warning",
  CLOSED: "info",
};

export function PerformancePage() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "HR_ADMIN";

  const [activeTab, setActiveTab] = useState("reviews");
  const [reviewView, setReviewView] = useState<"subject" | "reviewer">(
    "subject",
  );

  const [subjectReviews, setSubjectReviews] = useState<Review[]>([]);
  const [reviewerReviews, setReviewerReviews] = useState<Review[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [oneOnOnes, setOneOnOnes] = useState<OneOnOne[]>([]);
  const [cycles, setCycles] = useState<ReviewCycle[]>([]);
  const [loading, setLoading] = useState(true);

  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showCycleModal, setShowCycleModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, rr, g, asManager, asReport, c] = await Promise.all([
        listReviews({ subject: "me" }).catch(() => []),
        listReviews({ reviewer: "me" }).catch(() => []),
        listGoals({ owner: "me" }).catch(() => []),
        listOneOnOnes({ manager: "me" }).catch(() => []),
        listOneOnOnes({ report: "me" }).catch(() => []),
        isAdmin ? listReviewCycles().catch(() => []) : Promise.resolve([]),
      ]);
      setSubjectReviews(sr);
      setReviewerReviews(rr);
      setGoals(g);
      // Merge 1:1s deduped by id.
      const merged = new Map<string, OneOnOne>();
      [...asManager, ...asReport].forEach((o) => merged.set(o.id, o));
      setOneOnOnes(
        Array.from(merged.values()).sort((a, b) =>
          a.scheduled_at < b.scheduled_at ? 1 : -1,
        ),
      );
      setCycles(c);
    } catch {
      addToast("error", "Failed to load performance data");
    } finally {
      setLoading(false);
    }
  }, [addToast, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const tabs = useMemo(() => {
    const base = [
      {
        id: "reviews",
        label: "My Reviews",
        count: subjectReviews.length + reviewerReviews.length,
      },
      { id: "goals", label: "My Goals", count: goals.length },
      { id: "oneOnOnes", label: "1:1s", count: oneOnOnes.length },
    ];
    if (isAdmin) {
      base.push({ id: "cycles", label: "Cycles", count: cycles.length });
    }
    return base;
  }, [subjectReviews, reviewerReviews, goals, oneOnOnes, cycles, isAdmin]);

  const actions =
    activeTab === "goals" ? (
      <Button onClick={() => setShowGoalModal(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New Goal
      </Button>
    ) : activeTab === "cycles" && isAdmin ? (
      <Button onClick={() => setShowCycleModal(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New Cycle
      </Button>
    ) : undefined;

  const shownReviews =
    reviewView === "subject" ? subjectReviews : reviewerReviews;

  return (
    <motion.div {...pageTransition} className="space-y-6">
      <PageHeader
        title="Performance"
        subtitle="Reviews, goals, and 1:1s."
        actions={actions}
      />

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={setActiveTab}
        aria-label="Performance sections"
      />

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : activeTab === "reviews" ? (
        <>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={reviewView === "subject" ? "primary" : "secondary"}
              onClick={() => setReviewView("subject")}
            >
              About me ({subjectReviews.length})
            </Button>
            <Button
              size="sm"
              variant={reviewView === "reviewer" ? "primary" : "secondary"}
              onClick={() => setReviewView("reviewer")}
            >
              To complete ({reviewerReviews.length})
            </Button>
          </div>
          {shownReviews.length === 0 ? (
            <Card>
              <p className="text-sm text-text-muted text-center py-12">
                No reviews yet.
              </p>
            </Card>
          ) : (
            <Card padding={false}>
              <ul className="divide-y divide-border">
                {shownReviews.map((r) => (
                  <li
                    key={r.id}
                    className="px-4 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText
                        className="h-4 w-4 text-text-muted"
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-text-primary">
                          {r.review_type} review
                        </p>
                        <p className="text-xs text-text-muted truncate">
                          {r.submitted_at
                            ? `Submitted ${formatDate(r.submitted_at)}`
                            : "Not submitted yet"}
                        </p>
                      </div>
                    </div>
                    <Badge variant={REVIEW_STATUS_VARIANT[r.status]}>
                      {r.status.replace("_", " ")}
                    </Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      ) : activeTab === "goals" ? (
        goals.length === 0 ? (
          <Card>
            <p className="text-sm text-text-muted text-center py-12">
              No goals yet — set one to track your progress.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {goals.map((g) => (
              <Card key={g.id} className="space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Target
                      className="h-4 w-4 text-accent"
                      aria-hidden="true"
                    />
                    <h3 className="text-sm font-semibold text-text-primary">
                      {g.title}
                    </h3>
                  </div>
                  <Badge variant={GOAL_STATUS_VARIANT[g.status]}>
                    {g.status.replace("_", " ")}
                  </Badge>
                </div>
                {g.description && (
                  <p className="text-xs text-text-muted">{g.description}</p>
                )}
                <div>
                  <div className="flex justify-between text-xs text-text-muted mb-1">
                    <span>Progress</span>
                    <span>{Math.round(g.progress)}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-border overflow-hidden">
                    <div
                      className="h-full bg-accent transition-all"
                      style={{
                        width: `${Math.min(100, Math.max(0, g.progress))}%`,
                      }}
                    />
                  </div>
                </div>
                {g.target_date && (
                  <p className="text-xs text-text-muted">
                    Target {formatDate(g.target_date)}
                  </p>
                )}
              </Card>
            ))}
          </div>
        )
      ) : activeTab === "oneOnOnes" ? (
        oneOnOnes.length === 0 ? (
          <Card>
            <p className="text-sm text-text-muted text-center py-12">
              No 1:1s scheduled.
            </p>
          </Card>
        ) : (
          <Card padding={false}>
            <ul className="divide-y divide-border">
              {oneOnOnes.map((o) => (
                <li
                  key={o.id}
                  className="px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3">
                    <CalendarClock
                      className="h-4 w-4 text-text-muted"
                      aria-hidden="true"
                    />
                    <div>
                      <p className="text-sm font-medium text-text-primary">
                        {formatDate(o.scheduled_at, "MMM d, yyyy · HH:mm")}
                      </p>
                      <p className="text-xs text-text-muted">
                        {o.status || (o.completed_at ? "completed" : "scheduled")}
                      </p>
                    </div>
                  </div>
                  <Badge variant={o.completed_at ? "success" : "default"}>
                    {o.completed_at ? "Completed" : "Upcoming"}
                  </Badge>
                </li>
              ))}
            </ul>
          </Card>
        )
      ) : isAdmin ? (
        cycles.length === 0 ? (
          <Card>
            <p className="text-sm text-text-muted text-center py-12">
              No review cycles yet.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cycles.map((c) => (
              <Card key={c.id}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-semibold text-text-primary">
                    {c.name}
                  </h3>
                  <Badge variant={CYCLE_STATUS_VARIANT[c.status]}>
                    {c.status}
                  </Badge>
                </div>
                <p className="text-xs text-text-muted">
                  {formatDate(c.starts_at)} – {formatDate(c.ends_at)}
                </p>
              </Card>
            ))}
          </div>
        )
      ) : null}

      {showGoalModal && (
        <NewGoalModal
          onClose={() => setShowGoalModal(false)}
          onCreated={() => {
            setShowGoalModal(false);
            addToast("success", "Goal created");
            load();
          }}
        />
      )}
      {showCycleModal && (
        <NewCycleModal
          onClose={() => setShowCycleModal(false)}
          onCreated={() => {
            setShowCycleModal(false);
            addToast("success", "Cycle created");
            load();
          }}
        />
      )}
    </motion.div>
  );
}

function NewGoalModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<GoalCategory>("INDIVIDUAL");
  const [targetDate, setTargetDate] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await createGoal({
        title: title.trim(),
        description: description.trim() || null,
        category,
        target_date: targetDate || null,
      });
      onCreated();
    } catch {
      addToast("error", "Could not create goal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New goal" size="lg">
      <div className="space-y-3">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ship v2 of the reporting dashboard"
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Select
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value as GoalCategory)}
          options={[
            { value: "INDIVIDUAL", label: "Individual" },
            { value: "TEAM", label: "Team" },
            { value: "COMPANY", label: "Company" },
          ]}
        />
        <Input
          label="Target date"
          type="date"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={!title.trim()}
          >
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function NewCycleModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || !startsAt || !endsAt) return;
    setSaving(true);
    try {
      await createReviewCycle({
        name: name.trim(),
        description: description.trim() || null,
        starts_at: startsAt,
        ends_at: endsAt,
      });
      onCreated();
    } catch {
      addToast("error", "Could not create cycle");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="New review cycle" size="lg">
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Q2 2026 Reviews"
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Start date"
            type="date"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
          />
          <Input
            label="End date"
            type="date"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={saving}
            disabled={!name.trim() || !startsAt || !endsAt}
          >
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}
